/**
 * Where the site is mounted, and the only two functions that know about it.
 *
 * The published site is a GitHub *project* page, so it is served from a
 * subdirectory — `https://jadujoel.github.io/elsa/` — and the address bar and
 * the app disagree about what a page is called. The app speaks **routes**
 * (`/`, `/watch`, `/live`, `/contact`, `/music-video`), which is what
 * `App`'s `ROUTES`, `routePrewarm`'s `ROUTE_ASSETS` and every `<Link to>` use.
 * The browser speaks **hrefs** (`/elsa/watch`). `withBase` and `stripBase`
 * translate, and `src/router.tsx` is the only caller of either — so nothing
 * else in the app has to carry the base around.
 *
 * `__BASE__` is substituted by build.ts from the `BASE_PATH` environment
 * variable, and defaults to `/`. That default is not a fallback for a build
 * that forgot to set it — it is the correct answer everywhere the site is
 * served from a root: the dev server, `bun test`, and the four harnesses in
 * `scripts/` that serve `dist/` at `/`. `.github/workflows/deploy.yml` sets
 * `BASE_PATH: /elsa/` for the build that actually gets published.
 */

/** Substituted at build time. Absent in dev and under `bun test`; see below. */
declare const __BASE__: string | undefined;

/**
 * A base as the rest of this file wants it: leading and trailing slash, so
 * `/elsa`, `elsa/` and `/elsa/` are all `/elsa/`, and the site root is `/`.
 */
export function normalizeBase(base: string): string {
  const trimmed = base.trim();
  if (trimmed === "" || trimmed === "/") return "/";
  const rooted = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return rooted.endsWith("/") ? rooted : `${rooted}/`;
}

/**
 * `typeof` rather than a bare read: outside a `bun run build` the identifier is
 * never declared, and `typeof` is the one operator that may be applied to an
 * undeclared name without throwing.
 */
export const BASE: string = normalizeBase(typeof __BASE__ === "string" ? __BASE__ : "/");

/**
 * Route → href. `/watch` → `/elsa/watch`, and `/` → `/elsa/` rather than
 * `/elsa`, so the browser resolves the shell's relative asset URLs against the
 * deploy root instead of against its parent.
 */
export function withBase(route: string, base: string = BASE): string {
  if (base === "/" || !route.startsWith("/")) return route;
  if (route === "/") return base;
  // base ends with "/" and route begins with one; drop one of the two.
  return base.slice(0, -1) + route;
}

/**
 * Href → route. `/elsa/watch` → `/watch`, and both `/elsa/` and `/elsa` → `/`.
 *
 * A pathname outside the base is handed back untouched: it is not a route this
 * app can serve, and `App`'s `?? Home` is a better answer than a string mangled
 * to look like one.
 */
export function stripBase(pathname: string, base: string = BASE): string {
  if (base === "/") return pathname;
  const directory = base.slice(0, -1); // "/elsa/" -> "/elsa"
  if (pathname === directory) return "/";
  if (!pathname.startsWith(base)) return pathname;
  const rest = pathname.slice(directory.length); // keeps the leading "/"
  return rest === "/" ? "/" : rest;
}
